/**
 * 公共Vue过滤器
 * @module
 */
/**
 * 此类型仅为描述过滤器结构以便生成文档
 * @example
 * // 在vue模板中使用
 * // {{title|keyword("关键字")}}
 * // 在vue组件js中使用
 * // $options.filters.keyword(title,"关键字")
 * @typedef {Function} Filters
 * @property {Filter} json 将对象输出为json字符串
 * @property {Filter} fileSize 将一个文件尺寸转化为可读性更好的形式 {{value|fileSize}}
 * @property {Filter} parseChnDate2sec 尝试解析一个中文的时间描述
 * @property {Filter} xss xss过滤，转义掉有意义的html字符
 * @property {Filter} dateLimit 传入一个时间，返回一个可读性更好的时间文本 {{value|dateLimit [isShort=true]}}
 * @property {Filter} monthDate 格式化时间为系统默认格式。如年份跟当前一致则忽略年份
 * @property {Filter} dateFormat 格式化时间为系统默认格式
 * @property {Filter} gender 性别代码转换为中文描述.如：M=>男
 * @property {Filter} sliceText 截取字符串 {{value|sliceText length}}
 * @property {Filter} dicItemName 将字典值转化为名称 {{value|dicItemName Dictionary[]}}
 * @property {Filter} keyword 关键字着色 {{value|keyword keyword}}
 * @property {Filter} limitTime 将一个时间区间毫秒数转成易读的中午格式 {{value|limitTime}}
 *
 * @todo 如增加过滤器，需在此添加注释
 *
 */
import util from "@/assets/util"
import moment from "moment"
const GlobalWindow = window
export default {
	install (Vue) {
		Vue.filter("json", (value) => {
			return JSON.stringify(
				value,
				function (key, value) {
					if (typeof value === "function") {
						return value.toString()
					}
					return value
				},
				"\t"
			)
		})
		//将数字转换为货币格式
		//如  9400 => 9,400  可以加前缀 如 $, ￥
		//暂时不支持补小数位
		Vue.filter("currency", function (value, prefix = "") {
			return prefix + value
				.toString()
				.split("")
				.reverse()
				.map((item, index) => {
					if ((index + 1) % 3 === 0) return `,${item}`
					return item
				})
				.reverse()
				.join("")
		})
		Vue.filter("numFormat", util.numFormat)
		Vue.filter("fileSize", util.convertFileSize) //文件尺寸
		Vue.filter("limitTime", util.limitTime)
		Vue.filter("parseChnDate2sec", util.parseChnDate2sec)
		Vue.filter("xss", util.xss)
		//计算剩余时间
		Vue.filter("dateLimit", function (value, isShort = true) {
			if (!value) return "-"
			let endDate = new Date(value).getTime()
			let now = new Date().getTime()
			let limit = endDate - now
			if (Math.abs(limit) < 1000) return "刚刚"
			let prefix = limit > 0 ? "后" : "前"
			limit = Math.abs(limit)
			return util.limitTime(limit, isShort) + prefix
		})
		//如果年份跟当前一致则忽略年份
		//isDate 只输出日期
		Vue.filter("monthDate", function (value, isDate = false) {
			const format = isDate
				? GlobalWindow.SYSTEM_DATE_FORMAT_SHORT
				: GlobalWindow.SYSTEM_DATETIME_FORMAT_SHORT
			const fullFormat = isDate ? GlobalWindow.SYSTEM_DATE_FORMAT : GlobalWindow.SYSTEM_DATETIME_FORMAT
			if (!value) return ""
			//先兼容一下字符串格式的时间戳
			if (!isNaN(Number(value))) value = Number(value)
			const date = moment(value)
			const now = moment()
			if (date.year() === now.year()) {
				return date.format(format)
			}
			return date.format(fullFormat)
		})
		Vue.filter("datetimeFormat", function (value, format = GlobalWindow.SYSTEM_DATETIME_FORMAT) {
			if (!value) return ""
			console.log(value, format)
			//先兼容一下字符串格式的时间戳
			if (!isNaN(Number(value))) value = Number(value)
			const date = new Date(value)
			return moment(date).format(format)
		})
		Vue.filter("dateFormat", function (value, format = GlobalWindow.SYSTEM_DATE_FORMAT) {
			if (!value) return ""
			//先兼容一下字符串格式的时间戳
			if (!isNaN(Number(value))) value = Number(value)
			const date = new Date(value)
			return moment(date).format(format)
		})
		Vue.filter("gender", (state) => {
			const map = {
				M: "男",
				F: "女",
				1: "男",
				0: "女",
			}
			return map[state] || "-"
		})
		//字符串截取
		Vue.filter("sliceText", (str, length) => {
			if (!str) return ""
			str = str.toString()
			if (str.length <= length) return str
			return str.substr(0, length - 3) + "..."
		})
		//字典值转换为名称
		Vue.filter("dicItemName", (itemId, dic) => {
			//如果是字典编码,尝试去缓存读字典
			if (typeof dic === "string" && dic.startsWith("IDD")) {
				dic = GlobalWindow._dictionary_cache
					? GlobalWindow._dictionary_cache[dic]
					: undefined
			}
			if (!dic) return undefined
			const result = dic.filter((item) => item.value === itemId)
			if (!result.length) return ""
			return result[0].label
		})
		/**
		 * 关键字着色
		 */
		Vue.filter("keyword", function (value, keyword) {
			if (!value) return ""
			if (!keyword) return value
			value = util.safeHTML(value)
			keyword = util.escapeRegex(keyword)
			let regex = new RegExp("(" + keyword + ")", "ig")
			value = value.replace(regex, "<span class='keyword'>$1</span>")
			return value
		})
		/**
		 * 从一个map中获取指定key的值
		 * 通常用在工单列表的动态管道符
		 * 支持管道符过滤器
		 * 例子：
		 * //row = "createTime|monthDate"
		 * {{row|formatListColumn(bill)}}
		 */
		Vue.filter("formatListColumn", function (expr, billData, component = window.App) {
			const expression = util.parseVueComponentFilters(expr)
			// console.log(expr, expression, billData)
			try {
				return util.compileCode(expression)({
					_f: (filterName) => component.$options.filters[filterName],
					...billData,
				})
			} catch (e) {
				throw `Column Filter Expression Error[${expression}]:${e.toString()}`
			}
		})
	},
}
